//
// Copyright (c) 2021 Astute Global, Inc.
//

import i18next from "i18n/i18n";
import { Plugin } from "ckeditor5";
import {
  ButtonView,
  clickOutsideHandler,
  ContextualBalloon,
  _getSelectedModelContent,
} from "ckeditor5";

import customTemplateIcon from "img/svg/template.svg";
import CreateTemplateView from "./ui/createTemplateView";
import InsertTemplateView from "./ui/insertTemplateView";

export default class CustomTemplateUi extends Plugin {
  // noinspection JSUnusedGlobalSymbols
  static get requires() {
    return [];
  }

  // noinspection JSUnusedGlobalSymbols
  static get pluginName() {
    return "CustomTemplateUi";
  }

  init() {
    this._addToolbarButton();
    this.balloon = this.editor.plugins.get(ContextualBalloon);
  }

  get createTemplateView() {
    if (!this._createTemplateView) {
      this._createTemplateView = this._createCreateTemplateView();
    }
    return this._createTemplateView;
  }

  get insertTemplateView() {
    if (!this._insertTemplateView) {
      this._insertTemplateView = this._createInsertTemplateView();
    }
    return this._insertTemplateView;
  }

  get _isCreateTemplateViewInPanel() {
    return this._createTemplateView && this.balloon.hasView(this._createTemplateView);
  }

  get _isInsertTemplateViewInPanel() {
    return this._insertTemplateView && this.balloon.hasView(this._insertTemplateView);
  }

  _addCreateTemplateView() {
    if (this._isCreateTemplateViewInPanel) {
      return;
    }
    const view = this.editor.editing.view;
    const viewDocument = view.document;
    this.createTemplateView.disableCssTransitions();
    this.balloon.add({
      view: this.createTemplateView,
      position: {
        target: () => {
          return view.domConverter.viewRangeToDom(viewDocument.selection.getFirstRange());
        },
      },
    });
    if (this.balloon.visibleView == this.createTemplateView) {
      this.createTemplateView.focus();
    }
    this.createTemplateView.enableCssTransitions();
  }

  _addInsertTemplateView() {
    if (this._isInsertTemplateViewInPanel) {
      return;
    }
    const view = this.editor.editing.view;
    const viewDocument = view.document;
    this.insertTemplateView.disableCssTransitions();
    this.insertTemplateView.reset();
    this.balloon.add({
      view: this.insertTemplateView,
      position: {
        target: () => {
          return view.domConverter.viewRangeToDom(viewDocument.selection.getFirstRange());
        },
      },
    });
    if (this.balloon.visibleView == this.insertTemplateView) {
      this.insertTemplateView.focus();
    }
    this.insertTemplateView.enableCssTransitions();
  }

  _addToolbarButton() {
    const editor = this.editor;
    editor.ui.componentFactory.add("customTemplate", (locale) => {
      const createCommand = editor.commands.get("createCustomTemplate");
      const insertCommand = editor.commands.get("insertCustomTemplate");
      const button = new ButtonView(locale);
      button.set({
        isEnabled: true,
        label: i18next.t("ckeditor.customTemplate.buttonName"),
        icon: customTemplateIcon,
        tooltip: true,
      });
      button
        .bind("isEnabled")
        .toMany(
          [createCommand, insertCommand],
          "isEnabled",
          (createCommandEnabled, insertCommandEnabled) =>
            createCommandEnabled || insertCommandEnabled,
        );
      this.listenTo(button, "execute", () => this._showUi());
      return button;
    });
  }

  _cancelUi(command) {
    command.refresh();
    this._closeCreateTemplateView();
    this._closeInsertTemplateView();
  }

  _closeCreateTemplateView() {
    if (this._isCreateTemplateViewInPanel) {
      this.balloon.remove(this.createTemplateView);
      this.editor.editing.view.focus();
    }
  }

  _closeInsertTemplateView() {
    if (this._isInsertTemplateViewInPanel) {
      this.balloon.remove(this.insertTemplateView);
      this.editor.editing.view.focus();
    }
  }

  _createCreateTemplateView() {
    const editor = this.editor;
    const command = editor.commands.get("createCustomTemplate");
    const view = new CreateTemplateView(editor, command);
    view.on("submit", () => {
      this._closeCreateTemplateView();
      editor.execute("createCustomTemplate", {
        templateName: view.templateName,
      });
    });
    view.on("cancel", () => {
      this._cancelUi(command);
    });
    clickOutsideHandler({
      emitter: view,
      activator: () => this._isCreateTemplateViewInPanel,
      contextElements: [this.balloon.view.element],
      callback: () => this._cancelUi(command),
    });
    return view;
  }

  _createInsertTemplateView() {
    const editor = this.editor;
    const command = editor.commands.get("insertCustomTemplate");
    const view = new InsertTemplateView(editor, command);
    view.on("submit", () => {
      this._closeInsertTemplateView();
      const templatesToDelete = view.templatesToDelete;
      if (templatesToDelete && templatesToDelete.length > 0) {
        for (const template of templatesToDelete) {
          editor.execute("deleteCustomTemplate", template);
        }
      }
      editor.execute("insertCustomTemplate", {
        template: view.templateHtml,
      });
    });
    view.on("cancel", () => {
      this._cancelUi(command);
    });
    clickOutsideHandler({
      emitter: view,
      activator: () => this._isInsertTemplateViewInPanel,
      contextElements: [this.balloon.view.element],
      callback: () => this._cancelUi(command),
    });
    return view;
  }

  _showUi() {
    const view = this.editor.editing.view;
    const viewDocument = view.document;
    if (viewDocument.selection.isCollapsed) {
      this._addInsertTemplateView();
    } else {
      const model = this.editor.model;
      const selection = model.document.selection;
      const documentFragment = _getSelectedModelContent(model, selection);
      const rawHtml = this.editor.data.stringify(documentFragment);
      const command = this.editor.commands.get("createCustomTemplate");
      command.value = rawHtml;
      this._addCreateTemplateView();
    }
    this.balloon.showStack("main");
  }
}
